import { IonPage, useIonViewDidEnter, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { StreamChatChannelListWidget } from 'src/widgets/StreamChatChannelListWidget';

import 'stream-chat-react/dist/css/v2/index.css';
import '../../theme/stream-chat-theme.css';
import React, { useMemo, useState } from 'react';
import { DefaultStreamChatGenerics } from 'stream-chat-react';
import { environment } from 'src/enviroments';
import { getStreamChatToken } from 'src/api/stream/auth';
import { StreamChat, TranslationLanguages } from 'stream-chat';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Loading } from '@mindly/ui-components';
import { setIsActive } from 'src/redux/tabBar/reducer';
import { userSelector } from 'src/redux/user/selector';
import { transformClientIdToStreamUserId } from 'src/shared/utils/transformClientIdToStreamUserId';
import { setSupportButtonActive } from 'src/redux/supportButton/reducer';
import { useAnalytics } from 'src/shared';

const StreamChatChannelListPage: React.FC = () => {
  const currentUser = useSelector(userSelector);
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { trackPredefinedEvent } = useAnalytics();

  const [chatClient, setChatClient] = useState<StreamChat<DefaultStreamChatGenerics> | null>(null);
  const [didUserConnectInterrupt, setDidUserConnectInterrupt] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const transformedClientId = useMemo(() => transformClientIdToStreamUserId(currentUser?.id ?? ''), [currentUser?.id]);

  useIonViewDidEnter(() => {
    trackPredefinedEvent('CHAT_LIST_VIEWED', {
      is_new: true,
    });
  }, [trackPredefinedEvent]);

  useIonViewWillEnter(async () => {
    try {
      setIsLoading(true);
      if (!currentUser?.id) return;

      dispatch(setIsActive(false));
      const client = new StreamChat<DefaultStreamChatGenerics>(environment.STREAM_API_TOKEN, {
        enableInsights: true,
        enableWSFallback: true,
      });

      setDidUserConnectInterrupt(false);
      await client.connectUser(
        {
          id: transformedClientId,
          name: `${currentUser.display_name}${currentUser?.last_name ? ` ${currentUser?.last_name}` : ''}`,
          image: currentUser.avatar ?? null,
          language: i18n.language as TranslationLanguages,
        },
        getStreamChatToken
      );

      if (!didUserConnectInterrupt) {
        setChatClient(client);
      }
    } catch (error) {
      console.error(`Failed to connect user`, error);
    } finally {
      setIsLoading(false);
    }
  });

  useIonViewWillLeave(() => {
    try {
      if (!chatClient) return;

      const CLOSE_WEBSOCKET_TIMEOUT = 1000;
      setDidUserConnectInterrupt(true);
      chatClient.disconnectUser(CLOSE_WEBSOCKET_TIMEOUT);
      setChatClient(null);
    } catch (error) {
      console.error(`Failed to disconnect user`, error);
    }
  }, [chatClient]);

  useIonViewWillEnter(() => {
    dispatch(setSupportButtonActive(false));
  });

  return (
    <IonPage>
      <Loading isOpen={isLoading} />
      {chatClient && currentUser?.id && (
        <StreamChatChannelListWidget userId={transformedClientId} chatClient={chatClient} />
      )}
    </IonPage>
  );
};

export default StreamChatChannelListPage;
